import dayjs from "dayjs";

function toNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === "") continue;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const cleaned = String(v).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
    if (cleaned) {
      const n = parseFloat(cleaned[0]);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function todayIso() {
  return dayjs().format("YYYY-MM-DD");
}

function pickByName(list, name, nameKeys = ["name"]) {
  const target = normalizeText(name);
  if (!target || !Array.isArray(list) || list.length === 0) return null;

  const exact = list.find((item) =>
    nameKeys.some((key) => normalizeText(item?.[key]) === target),
  );
  if (exact) return exact;

  return (
    list.find((item) =>
      nameKeys.some((key) => {
        const value = normalizeText(item?.[key]);
        return value && (value.includes(target) || target.includes(value));
      }),
    ) || null
  );
}

function formatKg(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
}

/**
 * Label like: 1-5Kg (45*50*60inch)
 */
export function formatWeightBandLabel(weight, dimensions = [], currencySymbol = "") {
  if (!weight) return "";
  const min = formatKg(weight.minimumWeight);
  const max = formatKg(weight.maximumWeight);
  const range = `${min}-${max}Kg`;

  const dim =
    (Array.isArray(dimensions)
      ? dimensions.find((d) => Number(d.id) === Number(weight.dimensionId))
      : null) || null;

  const length = dim?.length ?? weight.length;
  const width = dim?.width ?? weight.width;
  const height = dim?.height ?? weight.height;

  let label = range;
  if (
    length != null &&
    width != null &&
    height != null &&
    length !== "" &&
    width !== "" &&
    height !== ""
  ) {
    label = `${range} (${formatKg(length)}*${formatKg(width)}*${formatKg(height)}inch)`;
  } else {
    const dimName =
      weight.dimensionName ||
      dim?.dimensionName ||
      dim?.name ||
      weight.weightDescription ||
      "";
    if (dimName) label = `${range} (${dimName})`;
  }

  const planName = weight.deliveryPlanName || weight.DeliveryPlanName || "";
  const planDaysLabel =
    weight.deliveryPlanDaysLabel ||
    weight.DeliveryPlanDaysLabel ||
    "";
  if (planName) {
    label = planDaysLabel
      ? `${label} · ${planName} (${planDaysLabel}d)`
      : `${label} · ${planName}`;
  }

  const cost = Number(weight.cost ?? weight.Cost);
  if (Number.isFinite(cost) && cost > 0) {
    const symbol = currencySymbol || "£";
    label = `${label} — ${symbol}${cost.toFixed(2)}/kg`;
  }
  return label;
}

export function weightBandCost(weight) {
  const cost = Number(weight?.cost ?? weight?.Cost);
  return Number.isFinite(cost) ? cost : null;
}

/** Weight charge = kg × band unit cost (e.g. 4kg × £50 = £200). */
export function weightChargeForKg(weightKg, bandUnitCost) {
  const kg = Number(weightKg);
  const unit = Number(bandUnitCost);
  if (!(Number.isFinite(kg) && kg > 0 && Number.isFinite(unit) && unit > 0)) {
    return null;
  }
  return Number((kg * unit).toFixed(2));
}

export function findWeightBandByKg(kg, weights = [], routingTypeId, deliveryPlanId) {
  const w = Number(kg);
  if (!(Number.isFinite(w) && w > 0) || !weights.length) return null;
  const options = weightsForRoute(weights, routingTypeId);
  const inRange = (row) => {
    const min = Number(row.minimumWeight);
    const max = Number(row.maximumWeight);
    return (
      Number.isFinite(min) &&
      Number.isFinite(max) &&
      w >= min &&
      w <= max
    );
  };
  const planId = Number(deliveryPlanId);
  if (planId > 0) {
    const planned = options.find(
      (row) => Number(row.deliveryPlanId) === planId && inRange(row),
    );
    if (planned) return planned;
  }
  return options.find(inRange) || null;
}

export function findWeightRowForProduct(product, weights = []) {
  if (!product || !weights.length) return null;
  const routeId = Number(product.routingTypeId) || 0;
  const scoped = weightsForRoute(weights, routeId);

  if (product.weightId) {
    const byId = scoped.find((w) => Number(w.id) === Number(product.weightId));
    if (byId) return byId;
  }

  const byKg = findWeightBandByKg(
    product.parcelWeight,
    scoped,
    routeId,
    product.deliveryPlanId,
  );
  if (byKg) return byKg;

  if (product.weightDescription) {
    return (
      scoped.find(
        (row) =>
          normalizeText(row.weightDescription) ===
          normalizeText(product.weightDescription),
      ) ||
      pickByName(scoped, product.weightDescription, ["weightDescription"])
    );
  }

  return null;
}

/**
 * Apply a weight-band selection: sets weightId, linked L/W/H from dimension master.
 * @param {{ preserveParcelWeight?: boolean }} options
 */
export function applyWeightBandToProduct(
  product,
  weightRow,
  dimensions = [],
  options = {},
) {
  if (!weightRow) return { ...product };

  const dim =
    dimensions.find((d) => Number(d.id) === Number(weightRow.dimensionId)) ||
    null;

  const parcelWeight = options.preserveParcelWeight
    ? product.parcelWeight
    : toNumber(weightRow.maximumWeight, weightRow.minimumWeight) ||
      Number(product.parcelWeight) ||
      "";

  return {
    ...product,
    weightId: weightRow.id,
    deliveryPlanId: weightRow.deliveryPlanId || weightRow.DeliveryPlanId || product.deliveryPlanId || "",
    weightDescription:
      weightRow.weightDescription ||
      `${formatKg(weightRow.minimumWeight)}-${formatKg(weightRow.maximumWeight)} kg`,
    parcelWeight,
    dimensionId: weightRow.dimensionId || product.dimensionId || "",
    dimensionName:
      weightRow.dimensionName ||
      dim?.dimensionName ||
      dim?.name ||
      product.dimensionName ||
      "",
    parcelLength:
      dim?.length != null && dim.length !== ""
        ? String(dim.length)
        : product.parcelLength || "",
    parcelWidth:
      dim?.width != null && dim.width !== ""
        ? String(dim.width)
        : product.parcelWidth || "",
    parcelHeight:
      dim?.height != null && dim.height !== ""
        ? String(dim.height)
        : product.parcelHeight || "",
    // Preserve customer-given dims (do not overwrite with weight-band / remesaure)
    customerParcelLength:
      product.customerParcelLength || product.parcelLength || "",
    customerParcelWidth:
      product.customerParcelWidth || product.parcelWidth || "",
    customerParcelHeight:
      product.customerParcelHeight || product.parcelHeight || "",
    // Never fall back to remesaure parcelWeight here — that collapses both weights
    customerParcelWeight: product.customerParcelWeight || "",
  };
}

export function weightsForRoute(weights = [], routingTypeId) {
  const routeId = Number(routingTypeId) || 0;
  if (!routeId) return weights;
  const filtered = weights.filter(
    (w) => !w.routeId || Number(w.routeId) === routeId,
  );
  return filtered.length ? filtered : weights;
}

/**
 * Map a history/detail/product row into editable product form state (names + any ids).
 * Measured L/W/H are only used when the API actually returns them — never from
 * dimensionName text (e.g. "Small Box") or dimension-master capacity.
 */
export function mapDetailRowToEditProduct(row = {}, parcel = {}) {
  const routingTypeId = toNumber(
    row.routingTypeId,
    row.routeId,
    row.RoutingTypeId,
    parcel.routingTypeId,
    parcel.routeId,
  );
  const cargoTypeId = toNumber(row.cargoTypeId, row.CargoTypeId);
  const itemTypeId = toNumber(
    row.itemTypeId,
    row.ItemTypeId,
    row.productTypeId,
  );
  const itemCategoryId = toNumber(
    row.itemCategoryId,
    row.ItemCategoryId,
    row.productGroupId,
  );

  // Do NOT parse weightDescription ("1-5 kg") as the numeric weight — that yields 1.
  const explicitWeight = toNumber(
    row.parcelWeight,
    row.weight,
    row.itemWeight,
  );

  // Only accept explicit measured dims — not "Small Box" / dimension band labels.
  const explicitLength = toNumber(row.parcelLength, row.length, row.itemLength);
  const explicitWidth = toNumber(row.parcelWidth, row.width, row.itemWidth);
  const explicitHeight = toNumber(row.parcelHeight, row.height, row.itemHeight);

  const customerLength = toNumber(
    row.customerParcelLength,
    row.CustomerParcelLength,
  );
  const customerWidth = toNumber(
    row.customerParcelWidth,
    row.CustomerParcelWidth,
  );
  const customerHeight = toNumber(
    row.customerParcelHeight,
    row.CustomerParcelHeight,
  );
  // Customer-given (booking-time). Weight must not fall back to remesaure ParcelWeight
  // when CustomerParcelWeight is missing — leave empty so UI can use remesaure-meta.
  const customerWeight = toNumber(
    row.customerParcelWeight,
    row.CustomerParcelWeight,
  );

  return {
    parcelDetailId:
      toNumber(row.parcelDetailId, row.parcelDetailsId, row.id) || undefined,
    routingTypeId: routingTypeId || "",
    parcelLength: explicitLength || "",
    parcelWidth: explicitWidth || "",
    parcelHeight: explicitHeight || "",
    parcelWeight: explicitWeight || "",
    // Customer-given (booking-time) — fall back to current Parcel* dims when not yet stored
    customerParcelLength: customerLength || explicitLength || "",
    customerParcelWidth: customerWidth || explicitWidth || "",
    customerParcelHeight: customerHeight || explicitHeight || "",
    customerParcelWeight: customerWeight || "",
    cargoTypeId: cargoTypeId || "",
    isExtraPackaging: Boolean(
      row.isExtraPackaging ?? row.IsExtraPackaging ?? false,
    ),
    itemTypeId: itemTypeId || "",
    itemCategoryId: itemCategoryId || "",
    shipmentArrivalDate:
      (row.shipmentArrivalDate || row.selectedShipmentArrivalDate || "")
        .toString()
        .slice(0, 10) || todayIso(),
    productQty: Math.max(
      1,
      toNumber(row.productQty, row.qty, row.quantity, row.itemQty, 1) || 1,
    ),
    uniqItemDescription:
      row.uniqItemDescription || row.itemDescription || row.description || "",
    itemValue: toNumber(row.itemValue, row.productValue, 0),
    parcelAdditionalInfo: row.parcelAdditionalInfo || "",
    routeName: row.routeName || "",
    cargoTypeName: row.cargoTypeName || "",
    itemTypeName: row.itemTypeName || row.productTypeName || "",
    itemCategoryName: row.itemCategoryName || row.productGroupName || "",
    weightDescription: row.weightDescription || "",
    dimensionName: row.dimensionName || "",
    weightId: toNumber(row.weightId, row.WeightId) || "",
    dimensionId: toNumber(row.dimensionId, row.DimensionId, row.dimentionId) || "",
  };
}

/**
 * Fill missing ids / LWH / weight from Route, Weight, Dimension, Cargo, Category lookups.
 */
export function enrichProductsWithLookups(products, lookups = {}) {
  const {
    routes = [],
    cargoTypes = [],
    itemCategories = [],
    itemTypes = [],
    weights = [],
    dimensions = [],
  } = lookups;

  return (products || []).map((product) => {
    const next = { ...product };

    if (!next.routingTypeId && next.routeName) {
      const route = pickByName(routes, next.routeName, [
        "routeName",
        "name",
      ]);
      if (route?.id != null) next.routingTypeId = route.id;
    }

    if (!next.cargoTypeId && next.cargoTypeName) {
      const cargo = pickByName(cargoTypes, next.cargoTypeName, [
        "cargoTypeName",
        "name",
      ]);
      if (cargo?.id != null) next.cargoTypeId = cargo.id;
    }

    // Weight master often carries routeId, itemCategoryId, dimensionId, min/max kg
    let weightRow = null;
    if (next.weightId) {
      weightRow = weights.find((w) => Number(w.id) === Number(next.weightId));
    }
    if (!weightRow && next.weightDescription) {
      const routeId = Number(next.routingTypeId) || 0;
      weightRow =
        weights.find(
          (w) =>
            normalizeText(w.weightDescription) ===
              normalizeText(next.weightDescription) &&
            (!routeId || Number(w.routeId) === routeId),
        ) ||
        pickByName(weights, next.weightDescription, ["weightDescription"]);
    }

    if (weightRow) {
      next.weightId = weightRow.id;
      if (!next.routingTypeId && weightRow.routeId) {
        next.routingTypeId = weightRow.routeId;
        next.routeName = next.routeName || weightRow.routeName || "";
      }
      if (!next.itemCategoryId && weightRow.itemCategoryId) {
        next.itemCategoryId = weightRow.itemCategoryId;
        next.itemCategoryName =
          next.itemCategoryName || weightRow.itemCategoryName || "";
      }
      if (!next.dimensionId && weightRow.dimensionId) {
        next.dimensionId = weightRow.dimensionId;
        next.dimensionName =
          next.dimensionName || weightRow.dimensionName || "";
      }
      if (!(Number(next.parcelWeight) > 0)) {
        // Prefer max of charged band for remesaure starting point (e.g. 1-5 → 5)
        next.parcelWeight =
          toNumber(weightRow.maximumWeight, weightRow.minimumWeight) || "";
      }
    }

    if (!next.itemCategoryId && next.itemCategoryName) {
      const cat = pickByName(itemCategories, next.itemCategoryName, [
        "name",
        "itemCategoryName",
      ]);
      if (cat?.id != null) next.itemCategoryId = cat.id;
    }

    if (!next.itemTypeId && next.itemTypeName) {
      const type = pickByName(itemTypes, next.itemTypeName, [
        "name",
        "itemTypeName",
      ]);
      if (type?.id != null) next.itemTypeId = type.id;
    }

    // Resolve dimension band id/name only — NEVER copy master L/W/H into the form.
    // Master sizes (e.g. Small Box 45×25×20) are capacity limits, not the measured
    // package the customer booked (often custom e.g. 5×5×5).
    let dimensionRow = null;
    if (next.dimensionId) {
      dimensionRow = dimensions.find(
        (d) => Number(d.id) === Number(next.dimensionId),
      );
    }
    if (!dimensionRow && next.dimensionName) {
      const routeId = Number(next.routingTypeId) || 0;
      dimensionRow =
        dimensions.find(
          (d) =>
            normalizeText(d.dimensionName) ===
              normalizeText(next.dimensionName) &&
            (!routeId || Number(d.routeId) === routeId),
        ) || pickByName(dimensions, next.dimensionName, ["dimensionName", "name"]);
    }

    if (dimensionRow) {
      next.dimensionId = dimensionRow.id;
      next.dimensionName =
        next.dimensionName || dimensionRow.dimensionName || "";
    }

    // Resolve item type name for display when we only have an id
    if (next.itemTypeId && !next.itemTypeName && itemTypes.length) {
      const type = itemTypes.find(
        (t) => Number(t.id) === Number(next.itemTypeId),
      );
      if (type) next.itemTypeName = type.name || type.itemTypeName || "";
    }

    // Default cargo to Air (1) only if still empty and Air exists
    if (!next.cargoTypeId && cargoTypes.length) {
      const air = pickByName(cargoTypes, "Air", ["cargoTypeName", "name"]);
      next.cargoTypeId = air?.id || cargoTypes[0]?.id || "";
    }

    return next;
  });
}

/**
 * Build editable products from parcel history + saved product lines.
 * Prefer GetParcelProductsForEdit (real itemTypeId / weightId / etc.).
 * GetParcelDetailsByParcelNo is payment-centric and must not replace them.
 */
export function buildEditProductsFromParcel(parcel, detailPayload, productLines) {
  const historyDetails = Array.isArray(parcel?.details) ? parcel.details : [];

  let apiLines = [];
  if (Array.isArray(productLines) && productLines.length > 0) {
    apiLines = productLines;
  } else if (Array.isArray(detailPayload)) {
    apiLines = detailPayload;
  } else if (Array.isArray(detailPayload?.details)) {
    apiLines = detailPayload.details;
  } else if (Array.isArray(detailPayload?.products)) {
    apiLines = detailPayload.products;
  } else if (Array.isArray(detailPayload?.parcelDetails)) {
    apiLines = detailPayload.parcelDetails;
  }

  // Product rows from GetParcelProductsForEdit have itemTypeId / weightId / routeId
  const looksLikeProducts =
    apiLines.length > 0 &&
    apiLines.some(
      (row) =>
        row.itemCategoryId != null ||
        row.itemTypeId != null ||
        row.weightId != null ||
        row.dimensionId != null ||
        row.routeId != null ||
        row.parcelLength != null ||
        row.weightDescription != null ||
        row.dimensionName != null ||
        row.uniqItemDescription != null,
    );

  const source = looksLikeProducts ? apiLines : historyDetails;

  if (source.length === 0) {
    return [
      mapDetailRowToEditProduct(
        {
          routingTypeId: parcel?.routingTypeId || parcel?.routeId,
          productQty: 1,
          uniqItemDescription: detailPayload?.description || "",
        },
        parcel,
      ),
    ];
  }

  return source.map((row, idx) => {
    const history = historyDetails[idx] || historyDetails[0] || {};
    // History supplies names (route/weight/dimension); product API supplies ids
    return mapDetailRowToEditProduct(
      looksLikeProducts ? { ...history, ...row } : { ...row, ...history },
      parcel,
    );
  });
}

export function sumPreviewTotal(previewRows = []) {
  return previewRows.reduce((sum, row) => {
    const amount =
      toNumber(
        row.totalCostWithVAT,
        row.totalCost,
        row.totalCostWithoutVAT,
        row.TotalCostWithVAT,
      ) || 0;
    return sum + amount;
  }, 0);
}

export function previousPayableFromParcel(parcel, costData = {}) {
  const fromCost = toNumber(
    costData.orderPayableAmount,
    costData.subTotal,
    costData.SubTotal,
  );
  if (fromCost > 0) return fromCost;

  return (parcel?.details || []).reduce(
    (sum, d) => sum + (toNumber(d.subTotal, d.orderPayableAmount) || 0),
    0,
  );
}

export function toPreviewRequest(product, isPickup) {
  return {
    routingTypeId: Number(product.routingTypeId) || 0,
    parcelLength: Number(product.parcelLength) || 0,
    parcelWidth: Number(product.parcelWidth) || 0,
    parcelHeight: Number(product.parcelHeight) || 0,
    parcelWeight: Number(product.parcelWeight) || 0,
    cargoTypeId: Number(product.cargoTypeId) || 1,
    isExtraPackaging: Boolean(product.isExtraPackaging),
    isPickUp: Boolean(isPickup),
    itemTypeId: Number(product.itemTypeId) || 0,
    itemCategoryId: Number(product.itemCategoryId) || 0,
    shipmentArrivalDate: product.shipmentArrivalDate || todayIso(),
    productQty: Math.max(1, Number(product.productQty) || 1),
    deliveryPlanId: Number(product.deliveryPlanId) || null,
  };
}

export function toSaveProduct(product) {
  // Parcel L/W/H: customer-given sizes (pricing dims live on WeightId/DimensionId).
  // ParcelWeight: remesaure / hub kg used for per-kg pricing.
  // CustomerParcel*: booking-time customer values — must stay distinct from remesaure kg.
  const customerLength =
    Number(product.customerParcelLength) ||
    Number(product.parcelLength) ||
    0;
  const customerWidth =
    Number(product.customerParcelWidth) || Number(product.parcelWidth) || 0;
  const customerHeight =
    Number(product.customerParcelHeight) ||
    Number(product.parcelHeight) ||
    0;
  const remesaureWeight = Number(product.parcelWeight) || 0;
  const customerWeight =
    Number(product.customerParcelWeight) > 0
      ? Number(product.customerParcelWeight)
      : remesaureWeight;

  const payload = {
    routingTypeId: Number(product.routingTypeId) || 0,
    parcelLength: customerLength,
    parcelWidth: customerWidth,
    parcelHeight: customerHeight,
    parcelWeight: remesaureWeight,
    customerParcelLength: customerLength,
    customerParcelWidth: customerWidth,
    customerParcelHeight: customerHeight,
    customerParcelWeight: customerWeight,
    cargoTypeId: Number(product.cargoTypeId) || 1,
    isExtraPackaging: Boolean(product.isExtraPackaging),
    itemTypeId: Number(product.itemTypeId) || 0,
    itemCategoryId: Number(product.itemCategoryId) || 0,
    deliveryPlanId: Number(product.deliveryPlanId) || null,
    shipmentArrivalDate: product.shipmentArrivalDate || todayIso(),
    productQty: Math.max(1, Number(product.productQty) || 1),
    uniqItemDescription: product.uniqItemDescription || "",
    itemValue: Number(product.itemValue) || 0,
    parcelAdditionalInfo: product.parcelAdditionalInfo || "",
  };
  if (product.parcelDetailId) {
    payload.parcelDetailId = Number(product.parcelDetailId);
  }
  return payload;
}

export function describeAdjustment(previousPayable, newPayable) {
  const prev = Number(previousPayable) || 0;
  const next = Number(newPayable) || 0;
  const delta = Number((next - prev).toFixed(2));
  if (Math.abs(delta) < 0.0001) {
    return { type: "None", amount: 0, delta: 0 };
  }
  if (delta > 0) {
    return { type: "Due", amount: delta, delta };
  }
  return { type: "Credit", amount: Math.abs(delta), delta };
}

export function formatAdjustmentMessage(previousPayable, newPayable) {
  const { type, amount } = describeAdjustment(previousPayable, newPayable);
  const prev = Number(previousPayable) || 0;
  const next = Number(newPayable) || 0;
  if (type === "Due") {
    return `Customer already paid/owed ${prev.toFixed(2)}. New total ${next.toFixed(2)}. A Due invoice of ${amount.toFixed(2)} will be created.`;
  }
  if (type === "Credit") {
    return `Customer already paid/owed ${prev.toFixed(2)}. New total ${next.toFixed(2)}. A Credit of ${amount.toFixed(2)} will be issued.`;
  }
  return `Totals are equal (${next.toFixed(2)}). Booking will be updated with no new invoice.`;
}
